import {
  joinWhatsappGroups,
  whatsappPickerGroupsQueryKey,
  whatsappPickerGroupsQueryOptions,
} from "@/shared/whatsappGroupQueries.ts";
import {
  defaultWhatsappGroupSelection,
  type JoinWhatsappGroupsResp,
  MAX_WHATSAPP_GROUP_ADDS,
  type WhatsappPickerGroup,
} from "@/types/whatsappGroups.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

type Options = {
  /**
   * Run once the join is queued, and once it fails. Set on the mutation rather
   * than per submit so they still run when the picker closes mid-request.
   */
  onJoined?: (resp: JoinWhatsappGroupsResp) => void;
  onJoinError?: (error: unknown) => void;
};

/**
 * The group list, the user's checks, and the join, shared by web's post-link
 * modal and native's.
 *
 * Checks start from the first `MAX_WHATSAPP_GROUP_ADDS` groups okthx isn't in
 * and are the user's from then on, never more than that cap. Nothing saves per
 * row: a pre-checked group the user never touched still has to be joined, so
 * the join is one explicit submit.
 */
export function useWhatsappGroupPicker({ onJoined, onJoinError }: Options = {}) {
  const queryClient = useQueryClient();
  const query = useQuery(whatsappPickerGroupsQueryOptions());
  const groups = useMemo(() => query.data?.groups ?? [], [query.data]);
  // Only groups that can still be joined count, so a group okthx joined since
  // the pick was made drops out of the count, the cap and the request.
  const joinableIds = useMemo(
    () => new Set(groups.filter((g) => !g.okthxIsMember).map((g) => g.id)),
    [groups],
  );
  // `undefined` until the user changes something, so the defaults follow the
  // list as it loads rather than freezing on an empty first render.
  const [picked, setPicked] = useState<ReadonlySet<string> | undefined>(undefined);
  const selected = useMemo(() => {
    if (!picked) return new Set(defaultWhatsappGroupSelection(groups));
    return new Set([...picked].filter((id) => joinableIds.has(id)));
  }, [picked, groups, joinableIds]);
  const atCap = selected.size >= MAX_WHATSAPP_GROUP_ADDS;

  const toggle = (groupId: string) =>
    setPicked((prev) => {
      const next = new Set([...(prev ?? selected)].filter((id) => joinableIds.has(id)));
      if (next.has(groupId)) next.delete(groupId);
      else if (next.size < MAX_WHATSAPP_GROUP_ADDS) next.add(groupId);
      return next;
    });

  const join = useMutation({
    mutationFn: () => joinWhatsappGroups({ groupIds: [...selected] }),
    onSuccess: (resp) => onJoined?.(resp),
    onError: (error) => onJoinError?.(error),
    // Marked stale without a refetch: each read is a live query from the
    // user's own WhatsApp, and the picker is done once the join is queued.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: whatsappPickerGroupsQueryKey, refetchType: "none" }),
  });

  return {
    query,
    groups,
    isSelected: (group: WhatsappPickerGroup) => group.okthxIsMember || selected.has(group.id),
    /** Unchecked rows are closed once the cap is reached; unchecking one reopens them. */
    isCapped: (group: WhatsappPickerGroup) =>
      atCap && !group.okthxIsMember && !selected.has(group.id),
    toggle,
    selectedCount: selected.size,
    /** More groups to add than one pick can take. */
    overCap: joinableIds.size > MAX_WHATSAPP_GROUP_ADDS,
    join,
  };
}
